import { useEffect, useRef, useState } from 'react';
import { AtSign } from 'lucide-react';
import { apiFetch } from '@/lib/api-client';
import { Textarea } from '@/components/ui/textarea';

// Textarea with @mention autocomplete (user ask 2026-07-31 — reminding a colleague, e.g. @Mathieu
// Cloos, on an internal question). Typing "@…" opens a picker fed by the live Uniticket user list
// (shared DB — the exact names Uniticket itself shows). Picking inserts a READABLE "@Display Name";
// finalizeMentions() converts those to Uniticket's native "@{uuid}" tokens at send time and returns
// the mentioned ids (sent as `mentioned_user_ids` — anticipatory, see uniticket-client).

export interface MentionUser { id: string; displayName: string; email: string | null }

/** Convert the readable "@Name" tokens back to Uniticket's "@{uuid}" + collect the ids still present
 *  (a mention the reviewer deleted from the text is dropped). The original readable text is left to
 *  the caller for local storage (remark history stays human-readable). */
export function finalizeMentions(text: string, picked: MentionUser[]): { text: string; ids: string[] } {
  let out = text;
  const ids: string[] = [];
  for (const u of picked) {
    const token = `@${u.displayName}`;
    if (out.includes(token)) {
      out = out.split(token).join(`@{${u.id}}`);
      ids.push(u.id);
    }
  }
  return { text: out, ids: [...new Set(ids)] };
}

const TERM_RE = /(^|\s)@([\p{L}\p{N} .'-]{0,40})$/u;

export function MentionTextarea({
  value,
  onChange,
  picked,
  onPicked,
  mentionsEnabled = true,
  disabled = false,
  placeholder,
  className,
  rows = 3,
  maxLength,
}: {
  value: string;
  onChange: (v: string) => void;
  /** Users picked so far (parent-owned so the send handler can finalize). */
  picked: MentionUser[];
  onPicked: (users: MentionUser[]) => void;
  /** Mentions only make sense on internal notes — when false this is a plain textarea. */
  mentionsEnabled?: boolean;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  rows?: number;
  maxLength?: number;
}) {
  const taRef = useRef<HTMLTextAreaElement>(null);
  const [suggestions, setSuggestions] = useState<MentionUser[]>([]);
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState<string | null>(null);

  function refreshTerm(next: string) {
    const el = taRef.current;
    const caret = el ? el.selectionStart : next.length;
    const m = TERM_RE.exec(next.slice(0, caret));
    setTerm(m ? m[2] : null);
  }

  useEffect(() => {
    if (!mentionsEnabled || term == null || term.trim().length < 2) {
      setOpen(false);
      setSuggestions([]);
      return;
    }
    const id = setTimeout(() => {
      apiFetch<{ data: MentionUser[] }>(`/uniticket-users?q=${encodeURIComponent(term.trim())}`)
        .then((r) => { setSuggestions(r.data); setOpen(r.data.length > 0); })
        .catch(() => { setSuggestions([]); setOpen(false); });
    }, 250);
    return () => clearTimeout(id);
  }, [term, mentionsEnabled]);

  function pick(u: MentionUser) {
    const el = taRef.current;
    const caret = el ? el.selectionStart : value.length;
    const before = value.slice(0, caret).replace(/@[\p{L}\p{N} .'-]{0,40}$/u, `@${u.displayName} `);
    onChange(before + value.slice(caret));
    if (!picked.some((p) => p.id === u.id)) onPicked([...picked, u]);
    setOpen(false);
    setTerm(null);
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(before.length, before.length);
    });
  }

  return (
    <div className="relative">
      <Textarea
        ref={taRef}
        value={value}
        onChange={(e) => { onChange(e.target.value); refreshTerm(e.target.value); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        disabled={disabled}
        placeholder={placeholder}
        rows={rows}
        maxLength={maxLength}
        className={className}
      />
      {open && (
        <div className="absolute left-2 top-full z-30 -mt-1 w-72 rounded-xl border border-slate-200 bg-white p-1 shadow-lg">
          {suggestions.map((u) => (
            <button
              key={u.id}
              type="button"
              // onMouseDown: fires BEFORE the textarea's blur closes the list.
              onMouseDown={(e) => { e.preventDefault(); pick(u); }}
              className="flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-left text-sm text-slate-700 hover:bg-brand-rose/[0.06]"
            >
              <AtSign className="h-3.5 w-3.5 shrink-0 text-brand-rose" />
              <span className="truncate font-medium">{u.displayName}</span>
              {u.email && <span className="ml-auto truncate text-[10px] text-slate-400">{u.email}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
